import React, { useContext, useMemo, lazy, Suspense, useEffect } from "react"
import { Helmet } from "react-helmet"
import {
  Route,
  Switch,
  RouteChildrenProps,
  useLocation,
  useRouteMatch
} from "react-router-dom"
import { useSelector } from "react-redux"
import { createActorContext } from "@xstate/react3"
import { MainContentLazyLoader as loading } from "../../../../mp-core/components"
import { RootState } from "../../../../mp-core/redux/store"
import { User } from "../../../../mp-database"
import { Roles } from "../../../../mp-utils/generic-utils"
import {
  StudyPlanBreadcrumbs,
  StudyPlanMachineListeners
} from "../../../components"

import createStudyPlanMachine from "../../../machines/studyPlan"
import { EocRouteParams } from "../../types"
import { Context, StudyPlanTargets } from "../../../machines/studyPlan/types"
import { ProgramsContext } from "../../../../mp-shared-components/ready-shared-components/components/Navs"
import { Box } from "@mui/material"
import { RouteParams } from "../../../../reporting/types"
import { REPORTING_ROUTE_MATCH } from "../../../../reporting/constants"

import { Program } from "../../../types"
import { programNameGetter } from "../../../helpers"
import { getStudyPlanV2CurrentTarget } from "../StudyPlansV2/helpers"
import { FiltersStateContext } from "../../../../reporting/providers"

export const StudyPlansLanding = lazy(() => import("../StudyPlansLanding"))

export const StudyPlansOverviewPage = lazy(
  () => import("../StudyPlansOverview")
)

export const TeacherStudyPlanPage = lazy(() => import("../TeacherStudyPlan"))

export const StudyPlanViewPage = lazy(() => import("../StudyPlanView"))

export const StudyPlanEditPage = lazy(() => import("../StudyPlanEdit"))

export const StudyPlanItemPreviewPage = lazy(
  () => import("../StudyPlanItemPreview")
)

export const StudyPlansV2 = lazy(() => import("../StudyPlansV2"))

const studyPlanMachine = createStudyPlanMachine()
// TODO: Use actual types for state machines ReturnType<typeof createStudyPlanMachine>
export const GlobalMachineContext = createActorContext(
  studyPlanMachine,
  null
  // state => console.log("[study-plans]", state.value, state.event, state.context)
)

type Props = RouteChildrenProps & {
  filteredProgram?: string
  filteredCourseSubject?: string
  isStudyPlanV2Enabled?: boolean
  studyPlanV2FeatureFlag?: boolean
}

const StudyPlansInit: React.FC<Props> = ({
  filteredProgram,
  filteredCourseSubject,
  isStudyPlanV2Enabled = false,
  studyPlanV2FeatureFlag = false,
  ...props
}) => {
  // Handle routing: old & new navigation
  const match =
    useRouteMatch<RouteParams>({
      path: REPORTING_ROUTE_MATCH,
      exact: false
    }) ?? props.match
  const isOldNav = match.path.includes("eoc")
  const basePath = isOldNav
    ? "/eoc/:program/:module(study-plans)"
    : "/reporting/teaching-tools/study-plan"
  const matchParams = isOldNav
    ? (match.params as EocRouteParams)
    : (match.params as RouteParams)
  const subPage = matchParams.subpage
  const entityType = isOldNav
    ? undefined
    : (matchParams as RouteParams).entityType

  // Get program and courseSubject from filters state (new navigation)

  const { user } = useSelector((state: RootState) => ({
    user: state.users.testUser as User
  }))
  const { currentProgram, currentProgramCourse, currentProgramSetter } =
    useContext(ProgramsContext)
  const { state: filtersState } = useContext(FiltersStateContext) ?? {}

  useEffect(() => {
    if (!isOldNav && filteredProgram) {
      currentProgramSetter(filteredProgram)
    }
  }, [isOldNav, filteredProgram])

  const [program, subject] = useMemo(() => {
    if (isOldNav) {
      const eocParams = props?.match?.params as EocRouteParams
      const subject = eocParams?.subject ?? null
      return [currentProgram?.toUpperCase() as Program, subject]
    } else {
      return [filteredProgram?.toUpperCase() as Program, filteredCourseSubject]
    }
  }, [
    isOldNav,
    currentProgram,
    currentProgramCourse,
    filteredProgram,
    filteredCourseSubject,
    props?.match?.params
  ])

  const programDisplayName = programNameGetter(
    program,
    currentProgramCourse?.programInfo?.displayName
  )

  const { search } = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])

  const currentTarget = useMemo(() => {
    if (isStudyPlanV2Enabled) {
      return getStudyPlanV2CurrentTarget(subPage)
    }

    if (isOldNav) {
      // For admins without a subject, go to landing
      if (!subject) {
        if (user.role === "District Admin" || user.role === "School Admin") {
          return StudyPlanTargets.Landing
        }
        return ""
      }

      if (!subPage) {
        if (user.role === "District Admin" || user.role === "School Admin") {
          return StudyPlanTargets.Overview
        }

        return ""
      }

      if (subPage === "view") {
        return StudyPlanTargets.ReviewPlan
      }

      return ""
    } else {
      if (subPage === "view" && entityType === "recommended") {
        return StudyPlanTargets.ViewRecommended
      } else if (subject && subPage === "view") {
        return StudyPlanTargets.ReviewPlan
      } else if (subject || subPage === "settings") {
        if (user.role === "Teacher") {
          return StudyPlanTargets.TeacherView
        }
        if (user.role === "District Admin" || user.role === "School Admin") {
          return StudyPlanTargets.Overview
        }
      } else {
        if (user.role === "Teacher") {
          return StudyPlanTargets.TeacherView
        }
        if (user.role === "District Admin" || user.role === "School Admin") {
          return StudyPlanTargets.Landing
        }
      }

      return ""
    }
  }, [subject, subPage, user.role, isOldNav, entityType])

  const initialContext: Partial<Context> = useMemo(() => {
    return {
      user,
      program,
      subject,
      currentTarget,
      isOldNav,
      ...(subject &&
        subject !== "settings" && {
          initialUrlSubject: isOldNav ? subject : null
        }),
      ...(subject === "settings" &&
        (user.role === "District Admin" || user.role === "School Admin") && {
          viewGlobalSettings: true
        }),
      ...(searchParams.get("classId") && {
        selectedEntity: {
          entityId: searchParams.get("classId"),
          entityName: searchParams.get("className")
        }
      }),

      //Study Plan V2
      isStudyPlanV2Enabled,
      studyPlanV2FeatureFlag,
      ...(isStudyPlanV2Enabled && { reportingFiltersState: filtersState || {} })
    }
  }, [
    currentTarget,
    subject,
    searchParams,
    user,
    program,
    isOldNav,
    isStudyPlanV2Enabled
  ])

  return (
    <>
      {program && (
        <Helmet>
          <title>
            Study Plans - {programDisplayName?.documentHead} - MasteryPrep
          </title>
        </Helmet>
      )}
      <GlobalMachineContext.Provider options={{ context: initialContext }}>
        <StudyPlanMachineListeners>
          {isStudyPlanV2Enabled ? (
            <StudyPlansV2 />
          ) : (
            <>
              {isOldNav && <StudyPlanBreadcrumbs user={user} />}
              <Box component="main" tabIndex={0}>
                <Switch>
                  <Route
                    exact
                    path="/eoc/:program/:module"
                    render={props => {
                      // This is only needed for the old navigation, remove once we no longer need to support it
                      if (user.role === Roles.Teacher) {
                        return (
                          <Suspense fallback={loading}>
                            <TeacherStudyPlanPage {...props} />
                          </Suspense>
                        )
                      }

                      if (
                        user.role === Roles.District_Admin ||
                        user.role === Roles.School_Admin
                      ) {
                        return (
                          <Suspense fallback={loading}>
                            <StudyPlansLanding {...props} />
                          </Suspense>
                        )
                      }
                    }}
                  />
                  {/* new vs old nav - nested routes under basePath */}
                  <Route
                    path={
                      isOldNav
                        ? `${basePath}/:subject`
                        : `${basePath}/:subpage?/:entityType?`
                    }
                    render={routeProps => (
                      <Switch>
                        {/* view page */}
                        <Route
                          path={
                            isOldNav
                              ? `${basePath}/:subject/view`
                              : `${basePath}/view/:entityType?`
                          }
                          render={props => (
                            <Suspense fallback={loading}>
                              <StudyPlanViewPage {...props} />
                            </Suspense>
                          )}
                        />
                        {/* edit page */}
                        <Route
                          path={
                            isOldNav
                              ? `${basePath}/:subject/edit`
                              : `${basePath}/edit`
                          }
                          render={props => (
                            <Suspense fallback={loading}>
                              <StudyPlanEditPage user={user} {...props} />
                            </Suspense>
                          )}
                        />
                        {/* preview page */}
                        <Route
                          path={
                            isOldNav
                              ? `${basePath}/:subject/preview`
                              : `${basePath}/preview`
                          }
                          render={props => (
                            <Suspense fallback={loading}>
                              <StudyPlanItemPreviewPage {...props} />
                            </Suspense>
                          )}
                        />
                        {/* overview/landing depending on role */}
                        <Route
                          exact
                          path={
                            isOldNav
                              ? `${basePath}/:subject`
                              : `${basePath}/:subpage?/:entityType?`
                          }
                          render={props => {
                            if (user.role === Roles.Teacher) {
                              if (subject) {
                                return (
                                  <Suspense fallback={loading}>
                                    <TeacherStudyPlanPage {...props} />
                                  </Suspense>
                                )
                              }
                              return (
                                <Suspense fallback={loading}>
                                  <TeacherStudyPlanPage {...props} />
                                </Suspense>
                              )
                            }

                            if (
                              user.role === Roles.District_Admin ||
                              user.role === Roles.School_Admin
                            ) {
                              if (subject) {
                                return (
                                  <Suspense fallback={loading}>
                                    <StudyPlansOverviewPage {...props} />
                                  </Suspense>
                                )
                              }
                              return (
                                <Suspense fallback={loading}>
                                  <StudyPlansLanding {...props} />
                                </Suspense>
                              )
                            }
                          }}
                        />
                      </Switch>
                    )}
                  />
                </Switch>
              </Box>
            </>
          )}
        </StudyPlanMachineListeners>
      </GlobalMachineContext.Provider>
    </>
  )
}

export default StudyPlansInit
